<script lang="ts" module>
	import type { MarkdownTokenList } from '$global/components/Markdown';

	export interface TalksSpeakersCardProps {
		name: string;
		/**
		 * Tokens a server load parsed, or a string for speakers fetched in the
		 * browser (the speakers page's infinite list).
		 */
		biography?: string | MarkdownTokenList;
		professional_title: string | null | undefined;
		image: string;
		twitter_handle?: string;
		instagram_handle?: string;
	}
</script>

<script lang="ts">
	import classNames from 'classnames';
	import { twMerge } from 'tailwind-merge';
	import { getLiveDropInfoContext } from '#lib/features/live-drop-info/LiveDropInfoContext/utils/getLiveDropInfoContext/index.js';
	import { GlobalRoutes } from '#lib/features/navigation/constants/global-routes.js';
	import { TalksRoutes } from '#lib/websites/talks/constants/routes.js';
	import { page } from '$app/state';
	import { ChevronRightIcon } from '$global/assets/icons/ChevronRightIcon';
	import { InstagramIcon } from '$global/assets/icons/InstagramIcon';
	import { TwitterXIcon } from '$global/assets/icons/TwitterXIcon';
	import { AccordionItem } from '$global/components/Accordion';
	import { IconLink } from '$global/components/IconLink';
	import { LazyMarkdown } from '$global/components/LazyMarkdown';
	import { LinkButton } from '$global/components/LinkButton';
	import { Txt } from '$global/components/Txt';
	import { track } from '$global/utils/track';

	interface Props {
		podcastOpenpanelPrefix: string;
		hasTalks: boolean;
		class?: string;
		dataCy: string;
		artistSlugs: string[];
		name: TalksSpeakersCardProps['name'];
		professionalTitle: TalksSpeakersCardProps['professional_title'];
		biography?: TalksSpeakersCardProps['biography'] | undefined | null;
		image: TalksSpeakersCardProps['image'];
		twitter?: TalksSpeakersCardProps['twitter_handle'] | undefined;
		insta?: TalksSpeakersCardProps['instagram_handle'] | undefined;
	}

	let {
		hasTalks,
		dataCy,
		artistSlugs,
		name,
		professionalTitle,
		podcastOpenpanelPrefix,
		biography = undefined,
		image,
		twitter = undefined,
		insta = undefined,
		...rest
	}: Props = $props();

	let readMore = $state(false);

	const toggleReadMore = () => {
		readMore = !readMore;
	};

	let socials = $derived([
		...(twitter
			? [
					{
						Icon: TwitterXIcon,
						href: `https://twitter.com/${twitter}`,
						className: 'w-5 h-5',
						label: 'Go to X',
						dataCy: 'twitter',
					},
				]
			: []),
		...(insta
			? [
					{
						Icon: InstagramIcon,
						href: `https://www.instagram.com/${insta}`,
						className: 'w-5 h-5',
						label: 'Go to Instagram',
						dataCy: 'instagram',
					},
				]
			: []),
	]);

	let dataCyPrefix = $derived(`${dataCy}-artist-card`);
	let artist_slug = $derived(
		name.toLowerCase().split(' ').join('-').replace('&', 'and')
	);

	const { liveDropInfoCtx } = getLiveDropInfoContext();

	let associatedPodcast = (
		liveDropInfoCtx?.liveDropInfo?.raw?.HENI_Talks_Podcast || []
	).find((podcast) =>
		podcast?.podcast_hosts?.some(
			(host) => host?.HENI_Talks_Speakers_id?.name === name
		)
	);
</script>

<AccordionItem
	class={twMerge('border-grey-200 h-fit border bg-gray-50', rest.class)}
	classes={{ titleButton: 'p-4 sm:px-6' }}
	dataCy={`${dataCyPrefix}-accordion`}
>
	{#snippet titleSlot()}
		<div class="flex items-center gap-4">
			<div
				class="h-14 w-14 max-w-[3.5rem] min-w-[3.5rem] overflow-hidden rounded-full"
			>
				<img
					alt="{name} avatar"
					src={image}
					class="h-full w-full object-cover"
				/>
			</div>
			<div class="w-full pr-4">
				<Txt
					variant="h5"
					class="w-full text-left"
					dataCy={`${dataCyPrefix}-artist-name`}>{name}</Txt
				>
				{#if professionalTitle}
					<Txt
						variant="label3"
						class="flex-col text-left text-gray-400"
						dataCy={`${dataCyPrefix}-label`}>{professionalTitle}</Txt
					>
				{/if}
			</div>
		</div>
	{/snippet}
	<div class="p-4 sm:p-6">
		{#if biography}
			<LazyMarkdown
				dataCy={`${dataCyPrefix}-biography`}
				source={biography}
				class={classNames('mb-4', {
					'line-clamp-4': !readMore,
				})}
			/>

			<button onclick={toggleReadMore}>
				<Txt
					variant="body1"
					class="mb-6 flex flex-col underline md:flex-row"
					dataCy={readMore
						? `${dataCyPrefix}-read-more`
						: `${dataCyPrefix}-read-less`}
				>
					{readMore ? 'Read less' : 'Read more'}
				</Txt>
			</button>
		{/if}

		<div class="flex flex-col gap-4 lg:flex-row">
			{#if hasTalks}
				<LinkButton
					class="w-full lg:w-auto"
					fullWidth
					dataCy={`${dataCyPrefix}-view-talks`}
					variant="primary"
					size="md"
					href={`${TalksRoutes.Gallery}?1=${encodeURIComponent(name)}&1_type=3`}
					>View Talks{#snippet trailing()}
						<ChevronRightIcon />
					{/snippet}</LinkButton
				>
			{/if}

			{#if associatedPodcast && page.route.id !== '/talks/podcasts/[slug]'}
				<LinkButton
					class="w-full lg:w-auto"
					fullWidth
					dataCy={`${dataCyPrefix}-view-podcast`}
					variant="primary"
					size="md"
					onclick={() => {
						track(`${podcastOpenpanelPrefix}_SPEAKER_VIEW_PODCAST_CLICK`, {
							slug: associatedPodcast?.slug,
							name,
						});
					}}
					href={`${TalksRoutes.Podcasts}/${associatedPodcast.slug}`}
					>View Podcast{#snippet trailing()}
						<ChevronRightIcon />
					{/snippet}</LinkButton
				>
			{/if}

			{#if artistSlugs?.includes(artist_slug)}
				<LinkButton
					class="w-full lg:w-auto"
					fullWidth
					dataCy={`${dataCyPrefix}-view-artists`}
					variant="secondary"
					size="md"
					href={`${GlobalRoutes.ArtistsHome}/${artist_slug}`}
					>View Artist page{#snippet trailing()}
						<ChevronRightIcon />
					{/snippet}</LinkButton
				>
			{/if}
			<div class="flex justify-center gap-2 md:justify-normal lg:ml-auto">
				{#each socials as { href, Icon, className, label, dataCy }}
					<IconLink
						{href}
						target="_blank"
						{label}
						dataCy={`${dataCyPrefix}-${dataCy}`}
						rel="noopener noreferrer"
						class="flex items-center"
					>
						<Icon color="gray-900" class={className} />
					</IconLink>
				{/each}
			</div>
		</div>
	</div>
</AccordionItem>
